import { useCallback, useEffect, useRef } from 'react'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useTripChatMobileOverlayOptional } from '../mobile/TripChatMobileOverlayContext'
import { getSubmitSearchItem } from '../components/ListingDrawerSearchUtils'
import { getHotelDateSelectionFromSearchState, type ListingDrawerHotelDateSelection } from '../components/ListingDrawerSearchDates'
import type { HotelListingSearchSubmitParams } from '../components/HotelListingDrawer/types'
import { createHotelSearchState } from '../components/HotelListingDrawer/hotelSearchState'

export interface ListingDrawerMobileHotelSearchParams {
  searchValue: string
  searchItem?: App.SearchItem
  dates: ListingDrawerHotelDateSelection
  rooms: Array<App.Occupants>
  maxGuests: number
  onSearch: (params: HotelListingSearchSubmitParams) => void
  onClose: () => void
}

/** One mounted editing session; unmounting discards drafts and pending extraction. */
function useListingDrawerMobileHotelSearch({ searchValue, searchItem, dates, rooms, maxGuests, onSearch, onClose }: ListingDrawerMobileHotelSearchParams) {
  const temporarySearchSubmitRef = useRef<App.TypeaheadSubmitRefData | null>(null)
  const overlay = useTripChatMobileOverlayOptional()
  const openOverlay = overlay?.openOverlay
  const closeOverlay = overlay?.closeOverlay
  const hotelSearchCloseRequest = overlay?.hotelSearchCloseRequest
  const initialCloseRequest = useRef(hotelSearchCloseRequest)
  const { globalSearchState: state, globalSearchDispatch: dispatch } = useGlobalSearchContext(
    createHotelSearchState({ searchValue, searchItem, hotelDateSelection: dates, occupancies: rooms }),
  )

  useEffect(() => {
    openOverlay?.('hotel-search')
    return () => closeOverlay?.('hotel-search', { shouldRequestOverlayClose: false })
  }, [closeOverlay, openOverlay])
  useEffect(() => {
    if (hotelSearchCloseRequest !== initialCloseRequest.current) onClose()
  }, [onClose, hotelSearchCloseRequest])

  const selectedRooms = state.occupancies ?? rooms
  const validRooms = selectedRooms.length > 0 && isValidOccupancy(...selectedRooms) && sumUpOccupancies(selectedRooms) <= maxGuests
  const validDates = state.dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    !!(state.checkinDate?.isValid() && state.checkoutDate?.isValid() && state.checkoutDate.isAfter(state.checkinDate, 'day'))
  const selectedLabel = state.searchItem?.format.mainText.trim() ?? ''
  const canSearch = !!selectedLabel && validDates
  const submit = useCallback(() => {
    if (!validRooms) {
      dispatch({ type: GlobalSearchStateActions.TOGGLE_ROOM_ERRORS, shown: true })
      return
    }
    if (!canSearch) return
    onSearch({
      searchValue: selectedLabel,
      searchItem: getSubmitSearchItem(selectedLabel, state.searchItem),
      hotelDateSelection: getHotelDateSelectionFromSearchState(state),
      occupancies: selectedRooms,
    })
    onClose()
  }, [canSearch, dispatch, onClose, onSearch, selectedLabel, selectedRooms, state, validRooms])

  return { state, dispatch, canSearch, submit, temporarySearchSubmitRef }
}

export default useListingDrawerMobileHotelSearch
